/* eslint-disable jira/integration/enforce-data-testid-usage */
import React, {
	useCallback,
	type ReactNode,
	type ComponentPropsWithRef,
	type RefAttributes,
} from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { type StyledComponentClass } from 'styled-components';
import AvatarGroupComponent from '@atlaskit/avatar-group';
import { token } from '@atlaskit/tokens';
import type { ItemType as FavouriteItemType } from '@atlassian/jira-favourite-change-provider/src/model/types.tsx';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { getPracticeLabel } from '@atlassian/jira-servicedesk-work-category/src/common/utils.tsx';
import { Link } from '@atlassian/react-resource-router';
import {
	BOARDS_ITEM_TYPE,
	FILTERS_ITEM_TYPE,
	DASHBOARDS_ITEM_TYPE,
	ISSUES_ITEM_TYPE,
	COMMENT_ITEM_TYPE,
	PROJECTS_ITEM_TYPE,
	QUEUES_ITEM_TYPE,
	PLANS_ITEM_TYPE,
} from '../../../constants/item-types.tsx';
import type { Item as ItemData } from '../../../types/item.tsx';
import { FavoriteButtonWithSSR } from '../../favourite-button/index.tsx';
import ItemIcon from './item-icon/index.tsx';
import messages from './messages.tsx';
import { convertToFavouriteItemType, getProjectType } from './utils.tsx';
import { WithCategory } from './with-category/index.tsx';

export type Props = {
	item: ItemData;
	index: number;
	showStar?: boolean;
	elemBefore?: ReactNode;
	onItemClick?: () => void;
};

const middot = String.fromCharCode(183);

const getAdditionalAttributes = (item: ItemData, type: string, intl: IntlShape) => {
	const { formatMessage } = intl;

	let location;
	let category;
	let { subtitle } = item;

	switch (type) {
		case BOARDS_ITEM_TYPE:
			subtitle = formatMessage(messages.board);
			location = undefined;
			if (
				typeof item.attributes !== 'undefined' &&
				typeof item.attributes.containerName === 'string'
			) {
				location = item.attributes.containerName;
			}
			break;
		case QUEUES_ITEM_TYPE:
			subtitle = formatMessage(messages.queue);
			location = '';
			if (typeof item.metadata === 'string') {
				location = item.metadata;
			}
			{
				const { attributes = {} } = item;
				const { queuesCategory } = attributes;
				const practiceLabel = queuesCategory != null ? getPracticeLabel(queuesCategory) : undefined;
				category = practiceLabel && formatMessage(practiceLabel);
			}
			break;
		case FILTERS_ITEM_TYPE:
			subtitle = formatMessage(messages.filter);

			break;
		case DASHBOARDS_ITEM_TYPE:
			subtitle = formatMessage(messages.dashboard);

			break;
		case ISSUES_ITEM_TYPE:
		case COMMENT_ITEM_TYPE:
			if (
				typeof item.attributes !== 'undefined' &&
				typeof item.attributes.containerName === 'string'
			) {
				location = item.attributes.containerName;
			}
			break;
		case PROJECTS_ITEM_TYPE:
			subtitle = getProjectType(item, intl);
			break;
		case PLANS_ITEM_TYPE:
			subtitle = formatMessage(messages.plan);
			break;
		default:
			subtitle = undefined;
			break;
	}

	if (typeof subtitle === 'string' && typeof location === 'string') {
		// @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'string'.
		subtitle = (
			<>
				<span>{subtitle}</span>
				<span>{middot}</span>
				<span>{location}</span>
			</>
		);
	} else if (typeof location === 'string') {
		subtitle = location;
	}

	return { category, subtitle };
};

const Item = ({ item, showStar, elemBefore, index, onItemClick }: Props) => {
	const intl = useIntl();

	// conform to lowercase, and remove the pluralised `s` if it exists
	const type = item.type.toLowerCase().replace(/s\s*$/, '');
	// for the favourite button we also need to ensure we pass the pluralised type
	const pluralisedType: FavouriteItemType = convertToFavouriteItemType(type);

	const { category, subtitle } = getAdditionalAttributes(item, type, intl);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
		});

		fireUIAnalytics(analyticsEvent, 'jiraHomeLink', {
			itemType: item.type,
			itemIndex: index,
			itemAction: item.action,
		});

		onItemClick && onItemClick();
	}, [createAnalyticsEvent, item.type, item.action, index, onItemClick]);

	return (
		<ItemLink
			href={item.url}
			key={item.url}
			onClick={onClick}
			data-test-id={`global-pages.home.ui.tab-container.tab.item-list.item-link#${
				item.type ? item.type.toString() : ''
			}-${item.id}`}
		>
			{showStar && item.entityId && typeof item.favourite !== 'undefined' && (
				<AfterIconsContainer>
					{elemBefore}
					<FavoriteButtonWithSSR
						itemId={item.entityId}
						itemType={pluralisedType}
						isFavoriteInitial={item.favourite}
						isSmall
					/>
				</AfterIconsContainer>
			)}
			{type === 'issue' || type === 'comment' ? (
				<ItemIcon type={type} url={item.avatarUrl} altText={item.attributes?.issueTypeName ?? ''} />
			) : (
				<ItemIcon type={type} url={item.avatarUrl} altText={item.type ?? ''} />
			)}
			<InnerWrapper>
				<Title>
					<WithCategory category={category}>{item.title}</WithCategory>
				</Title>
				<Subtitle>{subtitle}</Subtitle>
			</InnerWrapper>
			{item.status && <Status>{item.status}</Status>}
			{item.action && <Action>{item.action}</Action>}
			<AvatarGroupWrapper>
				{item.contributors && (
					<AvatarGroup
						data={item.contributors.map((contributor) => ({
							name: contributor.name,
							src: contributor.picture && contributor.picture,
							appearance: 'circle',
							size: 'medium',
							enableTooltip: true,
						}))}
					/>
				)}
			</AvatarGroupWrapper>
		</ItemLink>
	);
};
Item.whyDidYouRender = true;

export default Item;

// type LinkProps = ComponentPropsWithRef<typeof Link>;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InnerWrapper = styled2.span({
	flex: 6,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	marginTop: 0,
	marginRight: token('space.100'),
	marginBottom: 0,
	marginLeft: token('space.100'),
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.h4`
	font: ${token('font.body')};
	flex-grow: 1;
	margin: 0;
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Subtitle = styled.small`
	flex-grow: 1;
	margin: 0;
	font: ${token('font.body.small')};
	color: ${token('color.text.subtlest')};

	> span:not(:first-child):not(:last-child) {
		margin: 0 ${token('space.100')};
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Status = styled.span({
	flex: 1,
	margin: 0,

	color: token('color.text.subtlest'),
	verticalAlign: 'center',
	minWidth: '8em',
	font: token('font.body.small'),
	paddingRight: token('space.200'),
	textAlign: 'right',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Action = styled.span({
	flex: 1,
	margin: 0,

	color: token('color.text.subtlest'),
	verticalAlign: 'middle',
	minWidth: '8em',
	font: token('font.body.small'),
	paddingRight: token('space.200'),
	textAlign: 'right',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarGroupWrapper = styled.div({
	margin: 0,
	display: 'flex',
	flexGrow: 0,
	flexShrink: 0,
	justifyContent: 'flex-end',
	width: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AfterIconsContainer = styled2.div({
	display: 'flex',
	alignItems: 'center',
	marginRight: token('space.100'),
});

type LinkProps = ComponentPropsWithRef<typeof Link>;

// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ItemLink: StyledComponentClass<LinkProps & RefAttributes<any>, any> = styled(Link)({
	marginTop: 0,
	marginLeft: token('space.negative.100'),
	marginRight: token('space.negative.100'),
	flex: 1,
	padding: token('space.100'),
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-start',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	textDecoration: 'none',

	color: token('color.link'),
	'&:hover, &:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.link')} !important`,
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		textDecoration: 'none',
	},
	'&:active': {
		backgroundColor: token('color.background.neutral.subtle.pressed'),
		fill: 'rgba(222, 235, 255, 1)',
		color: 'rgba(0, 82, 204, 1)',
	},
});

// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarGroup = styled(AvatarGroupComponent)({
	justifyContent: 'flex-end',
	width: '200px',
});
